import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { tasksAPI, usersAPI } from '../../services/api';
import { taskSourceShort } from '../../utils/taskSource';
import { fmtDate, parseUTC } from '../../utils/dateUtils';
import { parseApiError } from '../../utils/errors';

const STATUS_COLORS = { pending: 'orange', in_progress: 'blue', completed: 'green', overdue: 'red', cancelled: 'gray' };
const PRIORITY_COLORS = { low: 'green', medium: 'blue', high: 'orange', urgent: 'red' };
const REVIEW_COLORS = { not_reviewed: 'gray', review_required: 'orange', approved: 'green', rejected: 'red' };

export default function AllTasks() {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [empFilter, setEmpFilter] = useState('');
  const [reviewFilter, setReviewFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.task_status = statusFilter;
    if (empFilter) params.assigned_to = empFilter;
    tasksAPI.list(params).then(({ data }) => setTasks(data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    usersAPI.list({ role: 'employee' }).then(({ data }) => setEmployees(data));
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [statusFilter, empFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const review = async (t, review_status) => {
    setActingId(t.id);
    try {
      await tasksAPI.hrUpdate(t.id, { review_status });
      setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, review_status } : x));
    } catch (err) {
      alert(parseApiError(err, 'Failed to update review'));
    } finally { setActingId(null); }
  };

  const shown = useMemo(() => {
    let list = [...tasks];
    if (reviewFilter) list = list.filter((t) => t.review_status === reviewFilter);
    if (fromDate) { const f = new Date(fromDate); list = list.filter((t) => parseUTC(t.created_at) >= f); }
    if (toDate) { const e = new Date(toDate); e.setHours(23, 59, 59); list = list.filter((t) => parseUTC(t.created_at) <= e); }
    const t = (v) => (parseUTC(v)?.getTime() || 0);
    const cmp = {
      created_desc: (a, b) => t(b.created_at) - t(a.created_at),
      created_asc: (a, b) => t(a.created_at) - t(b.created_at),
      deadline_asc: (a, b) => t(a.deadline) - t(b.deadline),
      deadline_desc: (a, b) => t(b.deadline) - t(a.deadline),
      completed_desc: (a, b) => t(b.end_time) - t(a.end_time),
    }[sortBy];
    return cmp ? list.sort(cmp) : list;
  }, [tasks, reviewFilter, fromDate, toDate, sortBy]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>All Tasks</h2>
        <Link to="/hr/tasks/assign" className="btn btn-primary">+ Assign Task</Link>
      </div>

      <div className="filter-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {['pending', 'in_progress', 'completed', 'overdue', 'cancelled'].map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
        <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)}>
          <option value="">All Reviews</option>
          {['not_reviewed', 'review_required', 'approved', 'rejected'].map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
        <select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)}>
          <option value="">All Employees</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </select>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>From
          <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} style={{ marginLeft: 4 }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>To
          <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} style={{ marginLeft: 4 }} />
        </label>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="deadline_asc">Deadline ↑</option>
          <option value="deadline_desc">Deadline ↓</option>
          <option value="completed_desc">Recently completed</option>
        </select>
        {(fromDate || toDate || reviewFilter) && (
          <button className="btn btn-ghost" onClick={() => { setFromDate(''); setToDate(''); setReviewFilter(''); }}>Clear</button>
        )}
      </div>

      {loading ? <div className="page-loading">Loading...</div> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th><th>Title</th><th>Assigned To</th><th>Status</th>
                <th>Priority</th><th>Review</th><th>Created</th><th>Deadline</th><th>Completed</th><th>Source</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr><td colSpan={11} className="text-center text-muted">No tasks found</td></tr>
              ) : shown.map((t) => (
                <tr key={t.id}>
                  <td>{t.id}</td>
                  <td><Link to={`/hr/tasks/${t.id}`} className="table-link">{t.title}</Link></td>
                  <td>{t.assigned_to?.full_name || '—'}</td>
                  <td><span className={`badge badge-${STATUS_COLORS[t.status]}`}>{t.status.replace('_', ' ')}</span></td>
                  <td><span className={`badge badge-${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span></td>
                  <td><span className={`badge badge-${REVIEW_COLORS[t.review_status]}`}>{t.review_status.replace('_', ' ')}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</td>
                  <td className={t.is_delayed ? 'text-danger' : ''} style={{ whiteSpace: 'nowrap' }}>{t.deadline ? fmtDate(t.deadline) : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{t.status === 'completed' && t.end_time ? fmtDate(t.end_time) : '—'}</td>
                  <td><span className="text-muted">{taskSourceShort(t)}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-success" disabled={actingId === t.id || t.review_status === 'approved'}
                        onClick={() => review(t, 'approved')}>Approve</button>
                      <button className="btn btn-sm btn-danger" disabled={actingId === t.id || t.review_status === 'rejected'}
                        onClick={() => review(t, 'rejected')}>Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
